import React, { SyntheticEvent, ReactNode, useState, useRef } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Checkbox } from "./inputs/Checkbox";

const useStyles = makeStyles(theme => ({
  checkbox: {
    color: "rgba(0, 0, 0, 0.54)"
  }
}));

export const ConfirmContext = React.createContext<{
  confirm: (message: string) => Promise<void>;
}>({
  confirm: async (_: string) => {}
});

type ConfirmDialogProps = {
  open: boolean;
  message: string;
  onAgreeClick: () => void;
  onDisagreeClick: () => void;
};

const ConfirmDialog = ({
  open,
  message,
  onAgreeClick,
  onDisagreeClick
}: ConfirmDialogProps) => {
  const [confirmed, setConfirm] = useState(false);
  const classes = useStyles();

  return (
    <Dialog open={open}>
      <DialogTitle>{"Confirmation"}</DialogTitle>
      <DialogContent>
        <Checkbox
          checked={confirmed}
          className={classes.checkbox}
          color="secondary"
          label={message}
          onChange={(e: SyntheticEvent, checked: boolean) => {
            setConfirm(checked);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onDisagreeClick();
            setConfirm(false);
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          disabled={!confirmed}
          onClick={() => {
            onAgreeClick();
            setConfirm(false);
          }}
          color="primary"
          autoFocus
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type ConfirmProviderProps = {
  children: ReactNode;
};

export const ConfirmProvider = (props: ConfirmProviderProps) => {
  const [open, setDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const deferResolve = useRef<any>(null);
  const deferReject = useRef<any>(null);

  const dispose = () => {
    setDialogOpen(false);
    deferResolve.current = null;
    deferReject.current = null;
  };

  const confirm = (message: string): Promise<void> => {
    setDialogOpen(true);
    setMessage(message);

    return new Promise((resolve, reject) => {
      deferResolve.current = resolve;
      deferReject.current = reject;
    }).then(dispose, () => {
      dispose();
      return Promise.reject("disagree");
    });
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {props.children}
      <ConfirmDialog
        open={open}
        message={message}
        onDisagreeClick={() => {
          if (!deferReject.current) return;
          deferReject.current();
        }}
        onAgreeClick={() => {
          if (!deferResolve.current) return;
          deferResolve.current();
        }}
      />
    </ConfirmContext.Provider>
  );
};
